import React, { Component } from "react";
import { FormattedMessage } from "react-intl";
import { connect, type ConnectedProps } from "react-redux";
import type { RouteComponentProps } from "react-router-dom";

import { getPatientId } from "~/actions/get_patient_id";
import { sendInvoiceRequest } from "~/actions/invoice";
import {
  PayerId,
  PAYMENT_SYSTEM_TYPE,
  PaymentMethod,
  PaymentOption,
  PaymentSystem,
  SBERBANK_DISCLAIMER,
} from "~/common/constants";
import { remoteLog } from "~/common/logging";
import { redirectTo } from "~/common/navigation";
import { isPatient } from "~/common/patient";
import { isDevelopment } from "~/common/utils";
import { Button } from "~/components/ui/button";
import { Layout } from "~/components/ui/layout";
import { Loader } from "~/components/ui/loader";
import { Portlet, PortletTitle } from "~/components/ui/portlet";
import { setDocumentTitle } from "~/hooks/use-document-title";
import { getExtraServices, selectServices } from "~/slices/services";
import type { RootState } from "~/store";
import type { TPatient } from "~/types/patient";
import type { TService } from "~/types/service";

const mapStateToProps = (state: RootState) => {
  return {
    patient: state.patient,
    invoice: state.invoice,
    services: selectServices(state),
  };
};

const mapDispatchToProps = {
  sendInvoiceRequest,
  getPatient: getPatientId,
  getExtraServices,
};

type PaymentInvoiceProps = PropsFromRedux &
  RouteComponentProps<{ patient_id: string }> & {
    /** Render WITHOUT the legacy `<Layout>` chrome, for embedding inside the redesign shell. */
    bareLayout?: boolean;
  };

type PaymentInvoiceAmount = {
  price: number;
  course: boolean;
  extra: Pick<TService, "id" | "price" | "tag"> | null;
};

type PaymentInvoiceState = {
  patient_id: number | null;
  amount: PaymentInvoiceAmount | null;
  canSubmit: boolean;
  showLoader: boolean;
};

class PaymentInvoice extends Component<PaymentInvoiceProps, PaymentInvoiceState> {
  constructor(props: PaymentInvoiceProps) {
    super(props);
    this.state = {
      patient_id: null,
      amount: null,
      canSubmit: false,
      showLoader: false,
    };
    this.requestToPay = this.requestToPay.bind(this);
    this.paymentOptions = this.paymentOptions.bind(this);
    this.setAmount = this.setAmount.bind(this);
  }

  componentDidCatch(e: Error) {
    remoteLog(e, "payment_invoice");
  }

  componentDidMount() {
    const patient_id = Number(this.props.match.params.patient_id);
    void this.props.getPatient(patient_id);
    void this.props.getExtraServices(patient_id);
    this.setState({ patient_id });
    setDocumentTitle("Он-лайн оплата");
  }

  componentDidUpdate(prevProps: PaymentInvoiceProps) {
    const { invoice } = this.props;
    const formUrl = "invoice" in invoice ? invoice.invoice?.formUrl : undefined;
    const prevFormUrl =
      "invoice" in prevProps.invoice ? prevProps.invoice.invoice?.formUrl : undefined;
    if (formUrl && formUrl !== prevFormUrl) {
      redirectTo(formUrl);
    }
  }

  setAmount(amount: PaymentInvoiceState["amount"]) {
    this.setState({ amount });
    this.setState({ canSubmit: true });
  }

  requestToPay() {
    if (this.state.amount && this.state.patient_id != null) {
      this.setState({ showLoader: true });
      this.setState({ canSubmit: false });
      void this.props.sendInvoiceRequest(this.state.patient_id, this.state.amount);
    }
  }

  paymentOptions(course: TPatient["course"]) {
    if (
      course &&
      course.payments &&
      Object.keys(course.payments).length > 0 &&
      course.payment_method == PaymentMethod.CARD
    ) {
      const { payment_option } = course;
      const { services } = this.props;
      const { payments } = course;
      const remain = payments["remain"];
      const next = payments["next"];
      const cbct = payments["cbct"];
      let extra: PaymentInvoiceAmount["extra"] = null;

      if (cbct == true) {
        const cbct_service = services.find(
          (service) => service.tag === "CBCT_ANALYSIS" && service.paid == false,
        );
        if (cbct_service) {
          extra = cbct_service;
        }
      }
      if (remain > 0 || next > 0) {
        if (payment_option != PaymentOption.ADVANCED) {
          return (
            <div className="form-group">
              <div className="radio-list">
                <span className="control-label">Доступные оплаты:</span>
                <label>
                  <div className="radio">
                    <span>
                      <input
                        type="radio"
                        name="payment_option"
                        value={1}
                        onChange={() => this.setAmount({ price: next, course: true, extra })}
                      />
                    </span>
                  </div>
                  {next} рублей
                </label>
                {next != remain ? (
                  <label>
                    <div className="radio">
                      <span>
                        <input
                          type="radio"
                          name="payment_option"
                          value={2}
                          onChange={() => this.setAmount({ price: remain, course: true, extra })}
                        />
                      </span>
                    </div>
                    Оплатить оставшуюся сумму целиком ({remain} рублей)
                  </label>
                ) : null}
              </div>
            </div>
          );
        } else {
          return (
            <div className="form-group">
              <div>
                <div className="radio-list">
                  <label>
                    <div className="radio">
                      <span>
                        <input
                          type="radio"
                          name="payment_option"
                          value={1}
                          onChange={() => this.setAmount({ price: next, course: true, extra })}
                        />
                      </span>
                    </div>
                    Оплатить сумму {next} рублей
                  </label>
                  <br />
                </div>
              </div>
            </div>
          );
        }
      }
      return null;
    }
  }

  renderExtraPayments(course: TPatient["course"]) {
    if (
      course.payments &&
      course.payments.extra &&
      Array.isArray(course.payments.extra) &&
      course.payments.extra.length > 0
    ) {
      const { extra } = course.payments;
      const extra_payments: React.ReactNode[] = [];
      extra.forEach((e, index) => {
        extra_payments.push(
          <label key={e.id}>
            <div className="radio">
              <span>
                <input
                  type="radio"
                  name="payment_option"
                  value={index}
                  onChange={() =>
                    this.setAmount({ price: e.price, course: false, extra: { ...e } })
                  }
                />
              </span>
            </div>
            <FormattedMessage id={e.tag} /> {e.price} рублей
          </label>,
        );
      });
      return (
        <div className="form-group">
          <div className="radio-list">
            <span className="control-label">Оплата дополнительных услуг:</span>
            {extra_payments}
          </div>
        </div>
      );
    }
  }

  render() {
    if (!isPatient(this.props.patient)) {
      return (
        <Layout size="md-8" chrome={!this.props.bareLayout}>
          <Loader />
        </Layout>
      );
    }

    if (window.location.protocol === "https:" || isDevelopment) {
      const { patient } = this.props;
      const { course } = patient;

      if (patient.payer_id == PayerId.DOCTOR && course.payment_method == PaymentMethod.CARD) {
        const { total_payments } = patient;

        return (
          <Layout size="md-8" chrome={!this.props.bareLayout}>
            <Portlet as="main">
              <PortletTitle iconClassName="icon-book-open">Он-лайн оплата</PortletTitle>

              <div className="portlet-body">
                <div>
                  <label>
                    Оплачено {total_payments.paid} из {total_payments.total} рублей
                  </label>
                </div>
                {this.paymentOptions(course)}
                {this.renderExtraPayments(course)}
                <div>
                  {PAYMENT_SYSTEM_TYPE == PaymentSystem.SBERBANK ? (
                    <div className="form-group">
                      <textarea
                        className="form-control"
                        rows={10}
                        readOnly={true}
                        defaultValue={SBERBANK_DISCLAIMER}
                      ></textarea>
                    </div>
                  ) : null}
                  <Button disabled={!this.state.canSubmit} onClick={() => this.requestToPay()}>
                    Оплатить
                  </Button>
                </div>
                {this.state.showLoader && (
                  <Loader msg="Сейчас вы будете перенаправлены на форму оплаты" />
                )}
              </div>
            </Portlet>
          </Layout>
        );
      }
    }

    return null;
  }
}

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;
export default connector(PaymentInvoice);
